import React, { useState } from 'react';
import { PRINT_ADS } from '../config';
import {
  getPrintAdPrice,
  getPrintAd,
  findPromo,
  getOnlyIssue,
  isPrintAdOffered,
  getSpecialEdition,
  formatOrderCutoff,
  getFirstBookableWednesday,
  fmtDollars,
} from '../utils/pricing';

const FULL_PAGE = PRINT_ADS.find(a => a.id === 'full_page');

// Returns the spec object for the currently selected orientation.
function getActiveSpec(ad, orientation) {
  if (!ad) return null;
  if (ad.specs.length === 1) return ad.specs[0];
  return ad.specs.find(s => s.orientation === orientation) || ad.specs[0];
}

// 'Wed, Nov 25'
function fmtDay(date) {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// The highlighted card for a one-issue special (e.g. the Season of Giving
// spread). The bar sits inside the radio so the whole card is one tap target,
// and it only turns orange-bordered when picked, like every other size.
function FeaturedOption({ ad, isSelected, isNonprofit, onSelect }) {
  const issue     = getOnlyIssue(ad.id);
  const edition   = getSpecialEdition(issue);
  const fullPages = getPrintAdPrice(FULL_PAGE, isNonprofit) * ad.pageEquivalent;

  return (
    <div
      className={`radio-option featured-option${isSelected ? ' selected' : ''}`}
      onClick={onSelect}
      role="radio"
      aria-checked={isSelected}
      tabIndex={0}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onSelect()}
    >
      <div className="featured-bar">
        <span>Special Edition</span>
        <span>{edition ? `${edition.name} · ` : ''}{fmtDay(issue)}</span>
      </div>
      <div className="featured-body">
        <input type="radio" readOnly checked={isSelected} tabIndex={-1} aria-hidden="true" />
        <div className="option-info">
          <div className="option-label">{ad.label}</div>
          <div className="featured-pitch">{ad.feature.pitch}</div>
          <div className="featured-compare">
            <span className="nowrap">Two separate full pages</span>{' '}
            <span className="nowrap">would be {fmtDollars(fullPages)}.</span>
          </div>
          <div className="option-spec featured-spec">{ad.feature.specLine}</div>
          <div className="featured-deadline">
            Order by <span className="nowrap">{formatOrderCutoff(issue)}</span>
          </div>
        </div>
        <div className="featured-price">
          <span className="option-price">{fmtDollars(getPrintAdPrice(ad, isNonprofit))}</span>
          <span className="featured-per">one issue</span>
        </div>
      </div>
    </div>
  );
}

export default function Step1Medium({ form, setForm, notice, onClearNotice, onNext, onBack, onDigitalOnly }) {
  const [error, setError] = useState('');

  function selectPrint(id) {
    const ad = PRINT_ADS.find(a => a.id === id);
    // Default to horizontal for multi-orientation ads, null otherwise.
    const defaultOrientation = ad?.specs.length > 1 ? 'horizontal' : null;
    const onlyIssue = getOnlyIssue(id);
    setForm(f => {
      const next = { ...f, printAd: id, medium: 'print', digitalAds: [], orientation: defaultOrientation };
      // A one-issue special runs once, in its own issue.
      if (onlyIssue) return { ...next, weeks: 1, weeksTouched: true, startDate: onlyIssue };
      // Coming off one, put the run back to the usual default.
      if (getOnlyIssue(f.printAd)) {
        return { ...next, weeks: 4, weeksTouched: false, startDate: getFirstBookableWednesday() };
      }
      return next;
    });
    setError('');
    if (onClearNotice) onClearNotice();
  }

  function setOrientation(e, orientation) {
    e.stopPropagation(); // Don't re-trigger radio selection
    setForm(f => ({ ...f, orientation }));
  }

  function handleNext() {
    if (!form.printAd || !isPrintAdOffered(getPrintAd(form.printAd), form.orgType)) {
      setError('Please select a print ad size to continue.');
      return;
    }
    onNext();
  }

  const isNonprofit = form.orgType === 'nonprofit';

  // Flag which sizes carry the flash sale, so someone who followed a sale link
  // can see at a glance that sizing up is what unlocks it.
  const promo = findPromo(form.promo);

  return (
    <div className="step-card">
      <img
        src="/lightning_and_coffee.jpg"
        alt="Hendersonville Lightning newspaper with coffee"
        className="step-hero-img"
      />

      <h2 className="step-heading">Choose your print ad size</h2>
      <p className="step-subheading">
        Ads run every Wednesday. You can add digital placements in the next step.
        {isNonprofit && (
          <span className="nonprofit-rate-badge"> Non-profit rates applied</span>
        )}
      </p>

      {notice && <div className="size-notice" role="status">{notice}</div>}

      {PRINT_ADS.filter(ad => isPrintAdOffered(ad, form.orgType)).map(ad => {
        const isSelected      = form.printAd === ad.id;

        if (ad.feature) {
          return (
            <FeaturedOption
              key={ad.id}
              ad={ad}
              isSelected={isSelected}
              isNonprofit={isNonprofit}
              onSelect={() => selectPrint(ad.id)}
            />
          );
        }

        const hasOrientations = ad.specs.length > 1;
        const activeSpec      = isSelected
          ? getActiveSpec(ad, form.orientation)
          : ad.specs[0]; // Show horizontal (first) spec when not yet selected
        const displayPrice    = getPrintAdPrice(ad, isNonprofit);
        const onSale          = !!promo && promo.eligiblePrintAds.includes(ad.id);

        return (
          <div
            key={ad.id}
            className={`radio-option${isSelected ? ' selected' : ''}`}
            onClick={() => selectPrint(ad.id)}
            role="radio"
            aria-checked={isSelected}
            tabIndex={0}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && selectPrint(ad.id)}
          >
            <input type="radio" readOnly checked={isSelected} tabIndex={-1} />

            <div className="option-info">
              <div className="option-label">
                {ad.label}
                {onSale && (
                  <span className="promo-badge">
                    ${promo.discountPerWeek}/wk off
                  </span>
                )}
              </div>

              {/* Orientation toggle — only visible when this ad is selected */}
              {isSelected && hasOrientations && (
                <div className="orientation-toggle" onClick={e => e.stopPropagation()}>
                  {ad.specs.map(spec => (
                    <button
                      key={spec.orientation}
                      type="button"
                      className={`orientation-btn${form.orientation === spec.orientation ? ' active' : ''}`}
                      onClick={e => setOrientation(e, spec.orientation)}
                    >
                      {spec.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="option-spec">
                {activeSpec.dims}&ensp;&middot;&ensp;{ad.format}
              </div>
            </div>

            <div className="option-price">${displayPrice}</div>
          </div>
        );
      })}

      {error && <p className="step-error">{error}</p>}

      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={onBack}>&larr; Back</button>
        <button className="btn btn-primary" onClick={handleNext}>
          Continue &rarr;
        </button>
      </div>

      <div className="digital-only-divider">
        <div className="digital-only-lead">Not running a print ad?</div>
        <button className="digital-only-link" onClick={onDigitalOnly}>
          Buy digital ads only &rarr;
        </button>
      </div>
    </div>
  );
}
